<template>
  <div class="dropzone">
    <slot />

    <div class="dropzone-visual">
      <upload-icon />
      <h3>Drag & Drop</h3>
      <p>Upload songs by dropping them in your library</p>
    </div>
    <div class="dropzone-overlay"></div>
  </div>
</template>

<script>
import UploadIcon from '@/components/common/UploadIcon.vue';
import useFileValidation from '@/composables/useFileValidation';
import useTriggerFileValidationAlert from '@/composables/useTriggerFileValidationAlert';
import { useUploadingSingleSongWithActionSheet } from '@/composables/useUploadingSingleSong';
import { useStore } from 'vuex';
import { ldInit } from 'launchdarkly-vue-client-sdk';
import { isTokenExpired } from '@/services/auth';

export default {
  name: 'Dropzone',

  components: {
    UploadIcon,
  },

  setup() {
    const store = useStore();
    if (store.state.auth?.user) {
      ldInit({
        user: {
          key: store.state.auth.user.sub,
          email: store.state.auth.user.email,
        },
      });
    }

    return {};
  },

  computed: {
    isActive() {
      const token = localStorage.getItem('crate.auth.access_token') || '';
      const user = JSON.parse(localStorage.getItem('crate.auth.user') || '{}');
      const isUserLoggedInAndValid =
        user.email && token && !isTokenExpired(token);

      return (
        this.$router.currentRoute.value.name === 'Library' &&
        isUserLoggedInAndValid
      );
    },
  },

  mounted() {
    this.bootDropzone();
  },

  unmounted() {
    const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
    events.forEach((event) => {
      document
        .querySelector('.dropzone')
        .removeEventListener(event, (e) => e.preventDefault());
    });
  },

  methods: {
    bootDropzone() {
      const dropzone = document.querySelector('.dropzone');
      const dropzoneOverlay = document.querySelector('.dropzone-overlay');
      const dropzoneVisual = document.querySelector('.dropzone-visual');

      let dropzoneVisualListener = (() => {
        let dropzoneCounter = 0;

        return {
          over: () => {
            if (dropzoneCounter === 0 && this.isActive) {
              dropzoneVisual.style.display = 'block';
              dropzoneOverlay.style.display = 'block';
            }
          },
          enter: () => {
            dropzoneCounter++;
            if (this.isActive) {
              dropzoneVisual.style.display = 'block';
              dropzoneOverlay.style.display = 'block';
            }
          },
          leave: () => {
            dropzoneCounter--;
            if (dropzoneCounter === 0) {
              dropzoneVisual.style.display = 'none';
              dropzoneOverlay.style.display = 'none';
            }
          },
        };
      })();

      dropzone.addEventListener('dragover', (event) => {
        event.stopPropagation();
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        dropzoneVisualListener.over();
      });

      dropzone.addEventListener('dragenter', (event) => {
        event.stopPropagation();
        event.preventDefault();
        dropzoneVisualListener.enter();
      });

      dropzone.addEventListener('dragleave', function (event) {
        event.stopPropagation();
        event.preventDefault();
        dropzoneVisualListener.leave();
      });

      // catch file drop and perform action on it
      dropzone.addEventListener('drop', async (e) => {
        e.stopPropagation();
        e.preventDefault();
        dropzoneVisualListener.leave();

        if (!this.isActive) {
          return;
        }

        let files = Object.values(e.dataTransfer.files ?? {});
        if (files.length === 0) {
          return;
        }

        const allFilesAreValid = files.every((file) => {
          return useFileValidation(file);
        });

        if (!allFilesAreValid) {
          useTriggerFileValidationAlert();
          return;
        }

        if (files.length === 1) {
          await useUploadingSingleSongWithActionSheet(files.pop());
        }

        const uploadingFiles = files.map((file) => {
          return {
            id: URL.createObjectURL(new Blob([])).substr(-36),
            file,
            uploading: false,
            percentage: 0,
            status: 'PENDING',
          };
        });

        await this.$store.dispatch('setUploadingFiles', uploadingFiles);
      });
    },
  },
};
</script>

<style scoped>
.dropzone {
  height: 100%;
}
.dropzone-overlay {
  display: none;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
}
.dropzone-visual {
  display: none;
  padding: 24px;
  position: absolute;
  width: 275px;
  left: calc(50% - 137.5px);
  top: calc(50% - 97px);
  background: rgba(96, 96, 96, 0.5);
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.33);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  text-align: center;
  z-index: 1;
}

.dropzone-visual h3 {
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
  color: #ffffff;
  margin-bottom: 8px;
}

.dropzone-visual p {
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 16px;
  color: #a1a1a1;
  margin-top: 0;
}
</style>
